// ─── SETTINGS SCREEN ───────────────────────────────────────────
const resizeInvoiceImage = (file, outputFormat = 'webp') => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith('image/')) {
    reject(new Error('Choose a PNG, JPEG, or WebP image.'));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const maxWidth = 420;
      const maxHeight = 180;
      const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      if (outputFormat === 'png') {
        resolve(canvas.toDataURL('image/png'));
        return;
      }
      const webp = canvas.toDataURL('image/webp', 0.86);
      resolve(webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Could not read this logo.'));
    img.src = reader.result;
  };
  reader.onerror = () => reject(new Error('Could not read this logo.'));
  reader.readAsDataURL(file);
});

const SettingsScreen = ({ user, onUserChange, onNavigate }) => {
  const sub = user?.subscription;
  const plan = user?.plan || {};
  const isFreePlan = plan.plan === 'free';
  const freeOrderLimit = Number(plan.orderLimit || 0);
  const freeOrdersUsed = Math.max(0, Number(plan.ordersUsed || 0));
  const freeUsageDetail = freeOrderLimit ? `${freeOrdersUsed}/${freeOrderLimit} orders used this month.` : 'Limited order access.';
  const isLifetime = sub?.plan === 'custom' && sub?.currentPeriodEnd === '2099-12-31';
  const isReferred = sub?.plan === 'referred';
  const planLabel = isFreePlan ? 'Starter' : isLifetime ? 'Lifetime' : isReferred ? 'Referred Pro' : (sub?.status === 'trialing' || ['yearly', 'monthly', 'trial'].includes(sub?.plan)) ? 'Pro' : '-';
  const statusLabel =
    isFreePlan ? 'Starter' :
    sub?.status === 'trialing' ? 'Free trial' :
    sub?.status === 'active' ? 'Active' :
    sub?.status === 'past_due' ? 'Past due' :
    sub?.status === 'cancelled' ? 'Cancelled' :
    sub?.status === 'expired' ? 'Expired' :
    sub?.status || 'None';
  const subscriptionLabel = isFreePlan ? 'Starter' : sub ? `${statusLabel}${planLabel !== '-' ? ` / ${planLabel}` : ''}` : 'None';
  const periodLabel =
    isFreePlan ? 'Order limit' :
    isLifetime ? 'Access' :
    isReferred ? 'Access' :
    sub?.status === 'trialing' ? 'Trial ends' :
    sub?.status === 'active' ? 'Renews' :
    sub?.status === 'past_due' ? 'Current period ends' :
    'Period ends';
  const subExpiry = isFreePlan ? freeUsageDetail : isLifetime ? 'Lifetime' : isReferred ? 'Partner referred' : (sub?.currentPeriodEnd || 'Waiting for Stripe');
  const statusColor = ['Active', 'Free trial', 'Starter'].includes(statusLabel) ? '#065F46' : sub?.status === 'past_due' ? '#92400E' : sub ? '#6B7280' : '#9CA3AF';
  const statusBg = ['Active', 'Free trial', 'Starter'].includes(statusLabel) ? '#F0FDF4' : sub?.status === 'past_due' ? '#FFFBEB' : '#F9FAFB';
  const hasActiveAccess = !isFreePlan;
  const canManageBilling = !!sub?.canManageBilling;
  const proBillingDetail = hasActiveAccess
    ? isReferred
      ? 'Your Referred Pro access is active through an Omnifill partner.'
      : sub?.status === 'trialing'
        ? `Your Pro trial is active${sub?.currentPeriodEnd ? ` until ${sub.currentPeriodEnd}` : ''}. ${canManageBilling ? 'Use Stripe billing to change plans, update payment, or cancel.' : 'No checkout action is needed right now.'}`
        : `Your Pro access is active${sub?.currentPeriodEnd && !isLifetime ? ` through ${sub.currentPeriodEnd}` : ''}. ${canManageBilling ? 'Use Stripe billing to change plans, update payment, or cancel.' : 'Contact support for billing changes.'}`
    : '$290/year with annual billing. Includes a 14-day trial and unlimited orders.';
  const proFeatures = hasActiveAccess
    ? ['Unlimited orders', 'Analytics', 'Automatic fulfillment routing', 'Support']
    : ['14-day free trial', 'Unlimited orders', 'Analytics', 'Automatic fulfillment routing', 'Support'];
  const [preferredCurrency, setPreferredCurrency] = React.useState(user?.preferredAnalyticsCurrency || '');
  const [convertedRevenueMixing, setConvertedRevenueMixing] = React.useState(user?.convertedRevenueMixing !== false);
  const [emailConfirmationSendAllSkus, setEmailConfirmationSendAllSkus] = React.useState(user?.emailConfirmationSendAllSkus === true);
  const [emailConfirmationBccSelf, setEmailConfirmationBccSelf] = React.useState(user?.emailConfirmationBccSelf === true);
  const [settingsSaving, setSettingsSaving] = React.useState(false);
  const [settingsError, setSettingsError] = React.useState('');
  const [orderDisplaySettings, setOrderDisplaySettings] = React.useState(null);
  const [orderDisplaySettingsLoading, setOrderDisplaySettingsLoading] = React.useState(true);
  const [orderDisplaySettingsSaving, setOrderDisplaySettingsSaving] = React.useState(false);
  const [orderDisplaySettingsError, setOrderDisplaySettingsError] = React.useState('');
  const [upgradeBusy, setUpgradeBusy] = React.useState('');
  const [upgradeError, setUpgradeError] = React.useState('');
  const [assistantUsage, setAssistantUsage] = React.useState(null);
  const [assistantUsageLoading, setAssistantUsageLoading] = React.useState(true);
  const [assistantUsageError, setAssistantUsageError] = React.useState('');
  const [agentInterface, setAgentInterface] = React.useState(null);
  const [agentLoading, setAgentLoading] = React.useState(true);
  const [agentSaving, setAgentSaving] = React.useState(false);
  const [agentError, setAgentError] = React.useState('');
  const [agentDraft, setAgentDraft] = React.useState({ enabled:false, requireApproval:true, maxActionsPerDay:10, allowedScopes:[] });
  const [newAgentName, setNewAgentName] = React.useState('');
  const [newAgentScopes, setNewAgentScopes] = React.useState([]);
  const [createdAgentToken, setCreatedAgentToken] = React.useState(null);
  const [apiKeys, setApiKeys] = React.useState([]);
  const [apiKeysLoading, setApiKeysLoading] = React.useState(true);
  const [apiKeysSaving, setApiKeysSaving] = React.useState(false);
  const [apiKeysError, setApiKeysError] = React.useState('');
  const [newApiKeyName, setNewApiKeyName] = React.useState('');
  const [newApiKeyCapabilities, setNewApiKeyCapabilities] = React.useState(['products', 'stock']);
  const [apiKeyDrafts, setApiKeyDrafts] = React.useState({});
  const [createdApiKey, setCreatedApiKey] = React.useState(null);
  const [gmailState, setGmailState] = React.useState(null);
  const [gmailLoading, setGmailLoading] = React.useState(true);
  const [gmailSaving, setGmailSaving] = React.useState(false);
  const [gmailError, setGmailError] = React.useState('');
  const [gmailNotice, setGmailNotice] = React.useState('');
  const [gmailDraft, setGmailDraft] = React.useState({
    enabled:false,
    ruleId:'',
    recipientText:'',
    subject:'Omnifill order export',
    message:'Attached is the latest Omnifill CSV export for {rule}.',
    triggerType:'interval',
    intervalMinutes:1440,
    markRouted:true,
    includeEmpty:false,
  });
  const [brevoState, setBrevoState] = React.useState(null);
  const [brevoLoading, setBrevoLoading] = React.useState(true);
  const [brevoSaving, setBrevoSaving] = React.useState(false);
  const [brevoTesting, setBrevoTesting] = React.useState(false);
  const [brevoError, setBrevoError] = React.useState('');
  const [brevoNotice, setBrevoNotice] = React.useState('');
  const [brevoForm, setBrevoForm] = React.useState({ apiKey:'', senderName:'', senderEmail:'', fallbackTemplateId:'' });
  const [invoiceSettings, setInvoiceSettings] = React.useState({
    businessName:'',
    address:'',
    dispatchAddress:'',
    email:'',
    phone:'',
    taxId:'',
    eori:'',
    invoicePrefix:'INV',
    defaultReasonExport:'Commercial sale',
    defaultIncoterm:'',
    vatStatement:'',
    defaultFreightStatement:'',
    signerName:'',
    defaultNote:'',
    logoData:'',
    signatureData:'',
  });
  const [invoiceSettingsLoading, setInvoiceSettingsLoading] = React.useState(true);
  const [invoiceSettingsSaving, setInvoiceSettingsSaving] = React.useState(false);
  const [invoiceSettingsError, setInvoiceSettingsError] = React.useState('');
  const [invoiceSettingsNotice, setInvoiceSettingsNotice] = React.useState('');
  const [activeSettingsPage, setActiveSettingsPage] = React.useState(() => {
    try {
      const requested = sessionStorage.getItem('omnifill_settings_page');
      if (requested) sessionStorage.removeItem('omnifill_settings_page');
      return requested || 'account';
    } catch {
      return 'account';
    }
  });
  const sectionStyle = { background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' };
  const sectionHeaderStyle = { padding:'13px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 };
  const inputStyle = { width:'100%', border:'1px solid #D1D5DB', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E' };
  const statusPillStyle = { display:'inline-flex', alignItems:'center', gap:6, border:'1px solid #BBF7D0', background:'#F0FDF4', color:'#065F46', borderRadius:8, padding:'5px 8px', fontSize:12, fontWeight:800, whiteSpace:'nowrap' };
  const neutralPillStyle = { display:'inline-flex', alignItems:'center', gap:6, border:'1px solid #E5E7EB', background:'#F9FAFB', color:'#6B7280', borderRadius:8, padding:'5px 8px', fontSize:12, fontWeight:800, whiteSpace:'nowrap' };
  const softButtonStyle = { padding:'8px 12px', background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, fontSize:13, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7, whiteSpace:'nowrap' };
  const primaryButtonStyle = { ...softButtonStyle, background:'#006FDD', color:'#fff', borderColor:'#006FDD' };
  const dangerButtonStyle = { ...softButtonStyle, color:'#B91C1C', borderColor:'#FECACA', background:'#FEF2F2' };
  const stableSettingsComponents = React.useMemo(() => {
    const SectionTitleComponent = ({ title, subtitle, right }) => (
      <div style={sectionHeaderStyle}>
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:900, color:'#1A1A2E' }}>{title}</div>
          {subtitle && <div style={{ fontSize:12, color:'#9CA3AF', marginTop:3, lineHeight:1.35 }}>{subtitle}</div>}
        </div>
        {right}
      </div>
    );

    const SettingsItemComponent = ({ title, detail, meta, action }) => (
      <div className="settings-row">
        <div className="settings-row-main">
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>{title}</div>
            {meta}
          </div>
          {detail && <div style={{ fontSize:12, color:'#6B7280', marginTop:3, lineHeight:1.4 }}>{detail}</div>}
        </div>
        {action && <div className="settings-action">{action}</div>}
      </div>
    );

    const BillingPlanCardComponent = ({ title, price, detail, features, tone = 'gray', action, badge }) => (
        <div style={{ border:`1px solid ${tone === 'blue' ? '#BFDBFE' : '#E5E7EB'}`, borderRadius:8, padding:14, background:tone === 'blue' ? '#F8FBFF' : '#fff', display:'flex', flexDirection:'column', gap:12 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:14, fontWeight:900, color:'#0F172A' }}>{title}</div>
              {badge}
            </div>
            <div style={{ fontSize:22, fontWeight:900, color:'#0F172A', marginTop:6, lineHeight:1 }}>{price}</div>
          </div>
          <div style={{ fontSize:12, color:'#64748B', lineHeight:1.45 }}>{detail}</div>
          <div style={{ display:'grid', gap:7 }}>
            {features.map(feature => (
              <div key={feature} style={{ fontSize:12, color:'#475569', lineHeight:1.35 }}>
                {feature}
              </div>
            ))}
          </div>
          {action && <div style={{ marginTop:'auto' }}>{action}</div>}
        </div>
    );

    const SettingsSectionComponent = ({ title, subtitle, right, children }) => (
      <section style={sectionStyle}>
        {(title || subtitle || right) && <SectionTitleComponent title={title} subtitle={subtitle} right={right}/>}
        {children}
      </section>
    );

    const SettingsPageTitleComponent = ({ title, subtitle, right }) => (
      <div className="settings-page-title-row">
        <div style={{ minWidth:0 }}>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {right}
      </div>
    );

    return {
      SettingsItem: SettingsItemComponent,
      BillingPlanCard: BillingPlanCardComponent,
      SettingsSection: SettingsSectionComponent,
      SettingsPageTitle: SettingsPageTitleComponent,
    };
  }, []);
  const { SettingsItem, BillingPlanCard, SettingsSection, SettingsPageTitle } = stableSettingsComponents;

  const loadAgentInterface = React.useCallback(async () => {
    setAgentLoading(true);
    setAgentError('');
    try {
      const { ok, data } = await api('GET', '/api/agent-interface');
      if (!ok) throw new Error(data?.error || 'Could not load agent controls.');
      setAgentInterface(data);
    } catch (err) {
      setAgentInterface(null);
      setAgentError(err?.message || 'Could not load agent controls.');
    } finally {
      setAgentLoading(false);
    }
  }, []);

  const loadApiKeys = React.useCallback(async () => {
    setApiKeysLoading(true);
    setApiKeysError('');
    try {
      const { ok, data } = await api('GET', '/api/keys');
      if (!ok) throw new Error(data?.error || 'Could not load API keys.');
      const normalizedKeys = Array.isArray(data) ? data : [];
      setApiKeys(normalizedKeys);
      setApiKeyDrafts(Object.fromEntries(normalizedKeys.map(key => [key.id, Array.isArray(key.capabilities) ? key.capabilities : []])));
    } catch (err) {
      setApiKeys([]);
      setApiKeysError(err?.message || 'Could not load API keys.');
    } finally {
      setApiKeysLoading(false);
    }
  }, []);

  const applyGmailDraft = React.useCallback(data => {
    const schedule = data?.schedule || null;
    setGmailDraft({
      enabled: !!schedule?.enabled,
      ruleId: schedule?.ruleId ? String(schedule.ruleId) : '',
      recipientText: schedule?.recipientText || (Array.isArray(schedule?.recipients) ? schedule.recipients.join(', ') : ''),
      subject: schedule?.subject || 'Omnifill order export',
      message: schedule?.message || 'Attached is the latest Omnifill CSV export for {rule}.',
      triggerType: schedule?.triggerType === 'new_order' ? 'new_order' : 'interval',
      intervalMinutes: Number(schedule?.intervalMinutes || 1440),
      markRouted: schedule?.markRouted !== false,
      includeEmpty: !!schedule?.includeEmpty,
    });
  }, []);

  const loadGmailSettings = React.useCallback(async () => {
    setGmailLoading(true);
    setGmailError('');
    try {
      const { ok, data } = await api('GET', '/api/gmail/status');
      if (!ok) throw new Error(data?.error || 'Could not load Gmail settings.');
      setGmailState(data);
      applyGmailDraft(data);
    } catch (err) {
      setGmailState(null);
      setGmailError(err?.message || 'Could not load Gmail settings.');
    } finally {
      setGmailLoading(false);
    }
  }, [applyGmailDraft]);

  const applyBrevoDraft = React.useCallback(data => {
    const connection = data?.connection || null;
    setBrevoForm({
      apiKey: '',
      senderName: connection?.senderName || '',
      senderEmail: connection?.senderEmail || '',
      fallbackTemplateId: connection?.fallbackTemplateId ? String(connection.fallbackTemplateId) : '',
    });
  }, []);

  const loadBrevoSettings = React.useCallback(async () => {
    setBrevoLoading(true);
    setBrevoError('');
    try {
      const { ok, data } = await api('GET', '/api/brevo/status');
      if (!ok) throw new Error(data?.error || 'Could not load Brevo settings.');
      setBrevoState(data.connection);
      applyBrevoDraft(data);
    } catch (err) {
      setBrevoState(null);
      setBrevoError(err?.message || 'Could not load Brevo settings.');
    } finally {
      setBrevoLoading(false);
    }
  }, [applyBrevoDraft]);

  const loadInvoiceSettings = React.useCallback(async () => {
    setInvoiceSettingsLoading(true);
    setInvoiceSettingsError('');
    try {
      const { ok, data } = await api('GET', '/api/invoice-settings');
      if (!ok) throw new Error(data?.error || 'Could not load invoice settings.');
      setInvoiceSettings(prev => ({ ...prev, ...(data?.settings || {}) }));
    } catch (err) {
      setInvoiceSettingsError(err?.message || 'Could not load invoice settings.');
    } finally {
      setInvoiceSettingsLoading(false);
    }
  }, []);

  const loadOrderDisplaySettings = React.useCallback(async () => {
    setOrderDisplaySettingsLoading(true);
    setOrderDisplaySettingsError('');
    try {
      const { ok, data } = await api('GET', '/api/order-table-settings');
      if (!ok) throw new Error(data?.error || 'Could not load order display settings.');
      setOrderDisplaySettings(data?.settings || {});
    } catch (err) {
      setOrderDisplaySettings(null);
      setOrderDisplaySettingsError(err?.message || 'Could not load order display settings.');
    } finally {
      setOrderDisplaySettingsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setPreferredCurrency(user?.preferredAnalyticsCurrency || '');
    setConvertedRevenueMixing(user?.convertedRevenueMixing !== false);
    setEmailConfirmationSendAllSkus(user?.emailConfirmationSendAllSkus === true);
    setEmailConfirmationBccSelf(user?.emailConfirmationBccSelf === true);
  }, [user?.preferredAnalyticsCurrency, user?.convertedRevenueMixing, user?.emailConfirmationSendAllSkus, user?.emailConfirmationBccSelf]);
  React.useEffect(() => {
    loadAgentInterface();
  }, [loadAgentInterface]);
  React.useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);
  React.useEffect(() => {
    loadGmailSettings();
  }, [loadGmailSettings]);
  React.useEffect(() => {
    loadBrevoSettings();
  }, [loadBrevoSettings]);
  React.useEffect(() => {
    loadInvoiceSettings();
  }, [loadInvoiceSettings]);
  React.useEffect(() => {
    loadOrderDisplaySettings();
  }, [loadOrderDisplaySettings]);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailResult = params.get('gmail');
    if (!gmailResult) return;
    if (gmailResult === 'connected') {
      setActiveSettingsPage('integrations');
      setGmailNotice('Gmail connected.');
      loadGmailSettings();
    } else if (gmailResult === 'error') {
      setActiveSettingsPage('integrations');
      const msg = params.get('msg') || 'Gmail connection failed.';
      const labels = {
        missing_refresh_token: 'Gmail did not return offline access. Try connecting again.',
        invalid_gmail_state: 'Gmail connection expired. Try again.',
        missing_gmail_params: 'Gmail did not return the expected details.',
        gmail_server_error: 'Gmail connection failed. Try again.',
      };
      setGmailError(labels[msg] || msg);
    }
    params.delete('gmail');
    params.delete('msg');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [loadGmailSettings]);
  React.useEffect(() => {
    const settings = agentInterface?.settings;
    if (!settings) return;
    const next = {
      enabled: !!settings.enabled,
      requireApproval: settings.requireApproval !== false,
      maxActionsPerDay: Number(settings.maxActionsPerDay || 10),
      allowedScopes: Array.isArray(settings.allowedScopes) ? settings.allowedScopes : [],
    };
    setAgentDraft(next);
    setNewAgentScopes(next.allowedScopes);
  }, [agentInterface?.settings]);
  React.useEffect(() => {
    let active = true;
    setAssistantUsageLoading(true);
    setAssistantUsageError('');
    api('GET', '/api/assistant/usage').then(({ ok, data }) => {
      if (!active) return;
      if (ok) {
        setAssistantUsage(data?.usage || null);
      } else {
        setAssistantUsage(null);
        setAssistantUsageError(data?.error || 'Could not load assistant usage.');
      }
      setAssistantUsageLoading(false);
    }).catch(() => {
      if (!active) return;
      setAssistantUsage(null);
      setAssistantUsageError('Could not load assistant usage.');
      setAssistantUsageLoading(false);
    });
    return () => { active = false; };
  }, []);
  const preferredCurrencySaved = user?.preferredAnalyticsCurrency || '';
  const preferredCurrencyDirty = preferredCurrency !== preferredCurrencySaved;
  const convertedRevenueMixingDirty = convertedRevenueMixing !== (user?.convertedRevenueMixing !== false);
  const emailConfirmationSendAllSkusDirty = emailConfirmationSendAllSkus !== (user?.emailConfirmationSendAllSkus === true);
  const emailConfirmationBccSelfDirty = emailConfirmationBccSelf !== (user?.emailConfirmationBccSelf === true);
  const analyticsSettingsDirty = preferredCurrencyDirty || convertedRevenueMixingDirty || emailConfirmationSendAllSkusDirty || emailConfirmationBccSelfDirty;
  const formatAssistantPct = value => {
    const pct = Number(value || 0);
    if (pct > 0 && pct < 1) return `${pct.toFixed(1)}%`;
    return `${Math.round(pct)}%`;
  };
  const assistantBudgetPct = assistantUsage?.budgetMicros > 0
    ? Math.min(100, Math.max(0, (Number(assistantUsage.usedMicros || 0) / Number(assistantUsage.budgetMicros || 1)) * 100))
    : 0;
  const assistantResetLabel = assistantUsage?.resetAt
    ? new Date(assistantUsage.resetAt).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })
    : '';
  const agentScopes = agentInterface?.scopeCatalog || [];
  const agentTokens = (agentInterface?.tokens || []).filter(token => token.status === 'active');
  const agentRequests = agentInterface?.requests || [];
  const pendingAgentRequests = agentRequests.filter(request => request.status === 'pending');
  const agentSettings = agentInterface?.settings || agentDraft;
  const formatAgentDate = value => {
    if (!value) return 'Never';
    const raw = String(value);
    const parsed = new Date(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
    if (Number.isNaN(parsed.getTime())) return raw;
    return parsed.toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };
  const scopeLabel = scopeId => agentScopes.find(scope => scope.id === scopeId)?.label || scopeId;

  const savePreferredCurrency = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    const { ok, data } = await api('PATCH', '/api/settings', {
      preferredAnalyticsCurrency: preferredCurrency || null,
      convertedRevenueMixing,
      emailConfirmationSendAllSkus,
      emailConfirmationBccSelf,
    });
    setSettingsSaving(false);
    if (!ok) {
      setSettingsError(data?.error || 'Could not save analytics currency');
      setPreferredCurrency(user?.preferredAnalyticsCurrency || '');
      setConvertedRevenueMixing(user?.convertedRevenueMixing !== false);
      setEmailConfirmationSendAllSkus(user?.emailConfirmationSendAllSkus === true);
      setEmailConfirmationBccSelf(user?.emailConfirmationBccSelf === true);
      return;
    }
    await onUserChange?.();
  };

  const setShowIgnoredOrders = async showIgnoredOrders => {
    if (!orderDisplaySettings || orderDisplaySettingsSaving) return;
    const previous = orderDisplaySettings;
    const next = { ...previous, showIgnoredOrders };
    setOrderDisplaySettings(next);
    setOrderDisplaySettingsSaving(true);
    setOrderDisplaySettingsError('');
    try {
      const { ok, data } = await api('PATCH', '/api/order-table-settings', next);
      if (!ok) throw new Error(data?.error || 'Could not save order display settings.');
      setOrderDisplaySettings(data?.settings || next);
    } catch (err) {
      setOrderDisplaySettings(previous);
      setOrderDisplaySettingsError(err?.message || 'Could not save order display settings.');
    } finally {
      setOrderDisplaySettingsSaving(false);
    }
  };

  const updateInvoiceSetting = (key, value) => {
    setInvoiceSettings(prev => ({ ...prev, [key]:value }));
    setInvoiceSettingsError('');
    setInvoiceSettingsNotice('');
  };

  const chooseInvoiceLogo = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setInvoiceSettingsError('');
    setInvoiceSettingsNotice('');
    try {
      const logoData = await resizeInvoiceImage(file);
      updateInvoiceSetting('logoData', logoData);
    } catch (err) {
      setInvoiceSettingsError(err?.message || 'Could not prepare this logo.');
    }
  };

  const chooseInvoiceSignature = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setInvoiceSettingsError('');
    setInvoiceSettingsNotice('');
    try {
      const signatureData = await resizeInvoiceImage(file, 'png');
      updateInvoiceSetting('signatureData', signatureData);
    } catch (err) {
      setInvoiceSettingsError(err?.message || 'Could not prepare this signature.');
    }
  };

  const saveInvoiceSettings = async () => {
    setInvoiceSettingsSaving(true);
    setInvoiceSettingsError('');
    setInvoiceSettingsNotice('');
    const { ok, data } = await api('PATCH', '/api/invoice-settings', invoiceSettings);
    setInvoiceSettingsSaving(false);
    if (!ok) {
      setInvoiceSettingsError(data?.error || 'Could not save invoice settings.');
      return;
    }
    setInvoiceSettings(prev => ({ ...prev, ...(data?.settings || {}) }));
    setInvoiceSettingsNotice('Invoice settings saved.');
  };

  const openDataRequestTicket = () => {
    try {
      sessionStorage.setItem('omnifill_support_draft', JSON.stringify({
        category: 'Account',
        subject: 'Account deletion or data purge request',
        message: 'I would like help with an account deletion or data purge request. Please confirm the next steps and any verification you need from me.',
      }));
    } catch {}
    onNavigate?.('support');
  };

  const startUpgrade = async (upgradePlan = 'yearly') => {
    setUpgradeBusy(upgradePlan);
    setUpgradeError('');
    try {
      const { ok, data } = await api('POST', '/api/billing/checkout', { plan: upgradePlan });
      if (ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setUpgradeError(data?.error || 'Could not start checkout. Please try again.');
    } catch {
      setUpgradeError('Could not start checkout. Please try again.');
    } finally {
      setUpgradeBusy('');
    }
  };

  const openBillingPortal = async () => {
    setUpgradeBusy('portal');
    setUpgradeError('');
    try {
      const { ok, data } = await api('POST', '/api/billing/portal');
      if (ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setUpgradeError(data?.error || 'Could not open billing portal. Please try again.');
    } catch {
      setUpgradeError('Could not open billing portal. Please try again.');
    } finally {
      setUpgradeBusy('');
    }
  };

  const toggleAgentScope = scopeId => {
    setAgentError('');
    setAgentDraft(prev => {
      const allowedScopes = prev.allowedScopes.includes(scopeId)
        ? prev.allowedScopes.filter(scope => scope !== scopeId)
        : [...prev.allowedScopes, scopeId];
      setNewAgentScopes(current => current.filter(scope => allowedScopes.includes(scope)));
      return { ...prev, allowedScopes };
    });
  };

  const toggleNewAgentScope = scopeId => {
    setCreatedAgentToken(null);
    setNewAgentScopes(prev => (
      prev.includes(scopeId)
        ? prev.filter(scope => scope !== scopeId)
        : [...prev, scopeId]
    ));
  };

  const saveAgentSettings = async () => {
    setAgentSaving(true);
    setAgentError('');
    setCreatedAgentToken(null);
    try {
      const { ok, data } = await api('PATCH', '/api/agent-interface/settings', agentDraft);
      if (!ok) throw new Error(data?.error || 'Could not save agent settings.');
      setAgentInterface(data);
    } catch (err) {
      setAgentError(err?.message || 'Could not save agent settings.');
    } finally {
      setAgentSaving(false);
    }
  };

  const createAgentToken = async () => {
    setAgentSaving(true);
    setAgentError('');
    setCreatedAgentToken(null);
    try {
      const { ok, data } = await api('POST', '/api/agent-interface/tokens', {
        name: newAgentName,
        scopes: newAgentScopes,
      });
      if (!ok) throw new Error(data?.error || 'Could not create agent token.');
      setAgentInterface(data);
      setCreatedAgentToken(data.createdToken || null);
      setNewAgentName('');
    } catch (err) {
      setAgentError(err?.message || 'Could not create agent token.');
    } finally {
      setAgentSaving(false);
    }
  };

  const revokeAgentToken = async tokenId => {
    setAgentSaving(true);
    setAgentError('');
    setCreatedAgentToken(null);
    try {
      const { ok, data } = await api('DELETE', `/api/agent-interface/tokens/${tokenId}`);
      if (!ok) throw new Error(data?.error || 'Could not revoke agent token.');
      setAgentInterface(data);
    } catch (err) {
      setAgentError(err?.message || 'Could not revoke agent token.');
    } finally {
      setAgentSaving(false);
    }
  };

  const reviewAgentRequest = async (requestId, decision) => {
    setAgentSaving(true);
    setAgentError('');
    try {
      const { ok, data } = await api('POST', `/api/agent-interface/requests/${requestId}/review`, { decision });
      if (!ok) throw new Error(data?.error || 'Could not review agent request.');
      setAgentInterface(data);
    } catch (err) {
      setAgentError(err?.message || 'Could not review agent request.');
    } finally {
      setAgentSaving(false);
    }
  };

  const copyCreatedAgentToken = async () => {
    if (!createdAgentToken?.token) return;
    try {
      await navigator.clipboard.writeText(createdAgentToken.token);
    } catch {}
  };

  const createApiKey = async () => {
    setApiKeysSaving(true);
    setApiKeysError('');
    setCreatedApiKey(null);
    try {
      const { ok, data } = await api('POST', '/api/keys', { name: newApiKeyName, capabilities: newApiKeyCapabilities });
      if (!ok) throw new Error(data?.error || 'Could not create API key.');
      setCreatedApiKey(data);
      setNewApiKeyName('');
      setNewApiKeyCapabilities(['products', 'stock']);
      await loadApiKeys();
    } catch (err) {
      setApiKeysError(err?.message || 'Could not create API key.');
    } finally {
      setApiKeysSaving(false);
    }
  };

  const saveApiKeySettings = async keyId => {
    setApiKeysSaving(true);
    setApiKeysError('');
    try {
      const { ok, data } = await api('PATCH', `/api/keys/${keyId}`, { capabilities: apiKeyDrafts[keyId] || [] });
      if (!ok) throw new Error(data?.error || 'Could not update API key.');
      await loadApiKeys();
    } catch (err) {
      setApiKeysError(err?.message || 'Could not update API key.');
    } finally {
      setApiKeysSaving(false);
    }
  };

  const revokeApiKey = async keyId => {
    setApiKeysSaving(true);
    setApiKeysError('');
    setCreatedApiKey(null);
    try {
      const { ok, data } = await api('DELETE', `/api/keys/${keyId}`);
      if (!ok) throw new Error(data?.error || 'Could not revoke API key.');
      await loadApiKeys();
    } catch (err) {
      setApiKeysError(err?.message || 'Could not revoke API key.');
    } finally {
      setApiKeysSaving(false);
    }
  };

  const copyCreatedApiKey = async () => {
    if (!createdApiKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdApiKey.key);
    } catch {}
  };

  const connectGmail = () => {
    setGmailError('');
    setGmailNotice('');
    window.location.href = '/api/gmail/connect';
  };

  const disconnectGmail = async () => {
    setGmailSaving(true);
    setGmailError('');
    setGmailNotice('');
    try {
      const { ok, data } = await api('DELETE', '/api/gmail/disconnect');
      if (!ok) throw new Error(data?.error || 'Could not disconnect Gmail.');
      setGmailState(data);
      applyGmailDraft(data);
      setGmailNotice('Gmail disconnected.');
    } catch (err) {
      setGmailError(err?.message || 'Could not disconnect Gmail.');
    } finally {
      setGmailSaving(false);
    }
  };

  const updateGmailDraft = patch => {
    setGmailError('');
    setGmailNotice('');
    setGmailDraft(prev => ({ ...prev, ...patch }));
  };

  const saveGmailSchedule = async () => {
    setGmailSaving(true);
    setGmailError('');
    setGmailNotice('');
    try {
      const { ok, data } = await api('PUT', '/api/gmail/export-schedule', {
        ...gmailDraft,
        ruleId: gmailDraft.ruleId ? Number(gmailDraft.ruleId) : null,
        intervalMinutes: Number(gmailDraft.intervalMinutes || 1440),
      });
      if (!ok) throw new Error(data?.error || 'Could not save email export.');
      setGmailState(data);
      applyGmailDraft(data);
      setGmailNotice('Email export saved.');
    } catch (err) {
      setGmailError(err?.message || 'Could not save email export.');
    } finally {
      setGmailSaving(false);
    }
  };

  const sendGmailExportNow = async () => {
    setGmailSaving(true);
    setGmailError('');
    setGmailNotice('');
    try {
      const { ok, data } = await api('POST', '/api/gmail/export-schedule/send-now', {});
      if (!ok) throw new Error(data?.error || 'Could not send email export.');
      setGmailState(data);
      applyGmailDraft(data);
      setGmailNotice(data.skipped ? 'No matching orders to email.' : `Email export sent with ${data.rowCount || 0} row${Number(data.rowCount || 0) === 1 ? '' : 's'}.`);
    } catch (err) {
      setGmailError(err?.message || 'Could not send email export.');
    } finally {
      setGmailSaving(false);
    }
  };

  const updateBrevoForm = patch => {
    setBrevoError('');
    setBrevoNotice('');
    setBrevoForm(prev => ({ ...prev, ...patch }));
  };

  const saveBrevoConnection = async () => {
    setBrevoSaving(true);
    setBrevoError('');
    setBrevoNotice('');
    try {
      const { ok, data } = await api('PUT', '/api/brevo/connection', {
        ...(brevoForm.apiKey.trim() ? { apiKey: brevoForm.apiKey.trim() } : {}),
        senderName: brevoForm.senderName.trim(),
        senderEmail: brevoForm.senderEmail.trim(),
        fallbackTemplateId: brevoForm.fallbackTemplateId.trim(),
      });
      if (!ok) throw new Error(data?.error || 'Could not save Brevo settings.');
      setBrevoState(data.connection);
      applyBrevoDraft(data);
      setBrevoNotice('Brevo connection saved.');
    } catch (err) {
      setBrevoError(err?.message || 'Could not save Brevo settings.');
    } finally {
      setBrevoSaving(false);
    }
  };

  const testBrevoConnection = async () => {
    setBrevoTesting(true);
    setBrevoError('');
    setBrevoNotice('');
    try {
      const { ok, data } = await api('POST', '/api/brevo/connection/test', {});
      if (!ok) throw new Error(data?.error || 'Brevo test failed.');
      setBrevoNotice(data?.message || 'Brevo API key is valid.');
      await loadBrevoSettings();
    } catch (err) {
      setBrevoError(err?.message || 'Brevo test failed.');
      await loadBrevoSettings();
    } finally {
      setBrevoTesting(false);
    }
  };

  const disconnectBrevoConnection = async () => {
    setBrevoSaving(true);
    setBrevoError('');
    setBrevoNotice('');
    try {
      const { ok, data } = await api('DELETE', '/api/brevo/connection');
      if (!ok) throw new Error(data?.error || 'Could not disconnect Brevo.');
      setBrevoState(data.connection);
      applyBrevoDraft(data);
      setBrevoNotice('Brevo disconnected.');
    } catch (err) {
      setBrevoError(err?.message || 'Could not disconnect Brevo.');
    } finally {
      setBrevoSaving(false);
    }
  };

  const settingsPages = [
    { id:'account', label:'Account', Icon:UserCircle },
    { id:'security', label:'Security', Icon:ShieldCheck },
    { id:'preferences', label:'Preferences', Icon:BarChart2 },
    { id:'invoices', label:'Invoices', Icon:FileText },
    { id:'integrations', label:'Integrations', Icon:Mail },
    { id:'billing', label:'Billing', Icon:CreditCard },
    { id:'assistant', label:'Assistant', Icon:Sparkles },
    { id:'apikeys', label:'API keys', Icon:Lock },
    { id:'agent', label:'Agent access', Icon:KeyRound },
    { id:'data', label:'Data requests', Icon:LifeBuoy },
  ];
  const gmailConnection = gmailState?.connection || null;
  const gmailRules = gmailState?.rules || [];
  const gmailSelectedRule = gmailRules.find(rule => String(rule.id) === String(gmailDraft.ruleId));
  const gmailCanSend = !!gmailConnection && !!gmailDraft.ruleId && !!gmailDraft.recipientText.trim();
  const activeSettingsConfig = settingsPages.find(page => page.id === activeSettingsPage) || settingsPages[0];

  const settingsPageContent = {
    account: (
      <>
        <SettingsPageTitle title="Account"/>
        <SettingsSection title="Profile">
          <SettingsItem title="Email address" detail={user?.email || '-'} meta={<span style={user?.emailVerified ? statusPillStyle : neutralPillStyle}>{user?.emailVerified ? 'Verified' : 'Not verified'}</span>}/>
        </SettingsSection>
      </>
    ),
    security: (
      <>
        <SettingsPageTitle title="Security"/>
        <SettingsSection title="Sign-in">
          <SettingsItem
            title="Password"
            detail={user?.passwordConfigured ? 'Password sign-in is available for this account.' : 'This account was created with Google sign-in. Add password setup before enforcing backup access.'}
            meta={<span style={user?.passwordConfigured ? statusPillStyle : neutralPillStyle}>{user?.passwordConfigured ? 'Configured' : 'Not set'}</span>}
          />
        </SettingsSection>
        <TwoFactorSettingsPanel user={user} onUserChange={onUserChange}/>
      </>
    ),
    preferences: (
      <>
        <SettingsPageTitle title="Preferences"/>
        <SettingsSection title="Dashboard defaults">
          <SettingsItem
            title="Preferred currency"
            detail="Auto uses the currency with the most orders in the selected analytics period."
            action={
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <select value={preferredCurrency} onChange={e => { setPreferredCurrency(e.target.value); setSettingsError(''); }} disabled={settingsSaving} style={{ ...inputStyle, width:220, cursor:settingsSaving ? 'wait' : 'pointer' }}>
                  <option value="">Auto</option>
                  {MANUAL_CURRENCY_OPTIONS.map(currency => (
                    <option key={currency.value} value={currency.value}>{currency.label}</option>
                  ))}
                </select>
                <button onClick={savePreferredCurrency} disabled={!analyticsSettingsDirty || settingsSaving} style={{ ...softButtonStyle, background: analyticsSettingsDirty ? '#006FDD' : '#F9FAFB', color: analyticsSettingsDirty ? '#fff' : '#9CA3AF', borderColor: analyticsSettingsDirty ? '#006FDD' : '#E5E7EB', cursor: analyticsSettingsDirty && !settingsSaving ? 'pointer' : 'not-allowed' }}>
                  {settingsSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            }
          />
          <SettingsItem
            title="Converted revenue mixing"
            detail="Convert revenue in other currencies into your preferred currency and include it in analytics totals, trends, average order value, and product revenue."
            action={
              <label style={{ display:'inline-flex', alignItems:'center', gap:9, fontSize:12, fontWeight:800, color:'#374151', cursor:settingsSaving ? 'wait' : 'pointer', whiteSpace:'nowrap' }}>
                <input
                  type="checkbox"
                  checked={convertedRevenueMixing}
                  disabled={settingsSaving}
                  onChange={event => { setConvertedRevenueMixing(event.target.checked); setSettingsError(''); }}
                />
                {convertedRevenueMixing ? 'On' : 'Off'}
              </label>
            }
          />
          <SettingsItem
            title="Send one confirmation email per product"
            detail="By default, an order with several email-enabled products sends one confirmation email using the highest-value product's template. Turn this on to send a separate email for each email-enabled product in the order instead."
            action={
              <label style={{ display:'inline-flex', alignItems:'center', gap:9, fontSize:12, fontWeight:800, color:'#374151', cursor:settingsSaving ? 'wait' : 'pointer', whiteSpace:'nowrap' }}>
                <input
                  type="checkbox"
                  checked={emailConfirmationSendAllSkus}
                  disabled={settingsSaving}
                  onChange={event => { setEmailConfirmationSendAllSkus(event.target.checked); setSettingsError(''); }}
                />
                {emailConfirmationSendAllSkus ? 'One per product' : 'One per order'}
              </label>
            }
          />
          <SettingsItem
            title="Send me a copy of order confirmation emails"
            detail={`Bcc ${user?.email || 'your account email'} on every order confirmation email, with the exact same content and parameters the customer receives.`}
            action={
              <label style={{ display:'inline-flex', alignItems:'center', gap:9, fontSize:12, fontWeight:800, color:'#374151', cursor:settingsSaving ? 'wait' : 'pointer', whiteSpace:'nowrap' }}>
                <input
                  type="checkbox"
                  checked={emailConfirmationBccSelf}
                  disabled={settingsSaving}
                  onChange={event => { setEmailConfirmationBccSelf(event.target.checked); setSettingsError(''); }}
                />
                {emailConfirmationBccSelf ? 'On' : 'Off'}
              </label>
            }
          />
          <SettingsItem
            title="Show ignored orders"
            detail="Ignored orders are hidden from the Orders table by default. Turn this on when you need to review or restore them."
            action={
              orderDisplaySettingsLoading ? (
                <span style={{ fontSize:12, color:'#9CA3AF' }}>Loading...</span>
              ) : (
                <label style={{ display:'inline-flex', alignItems:'center', gap:9, fontSize:12, fontWeight:800, color:'#374151', cursor:orderDisplaySettingsSaving || !orderDisplaySettings ? 'wait' : 'pointer', whiteSpace:'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={orderDisplaySettings?.showIgnoredOrders === true}
                    disabled={orderDisplaySettingsSaving || !orderDisplaySettings}
                    onChange={event => setShowIgnoredOrders(event.target.checked)}
                  />
                  {orderDisplaySettings?.showIgnoredOrders ? 'Shown' : 'Hidden'}
                </label>
              )
            }
          />
          {settingsError && <div style={{ fontSize:12, color:'#DC2626', fontWeight:700, padding:'0 16px 14px' }}>{settingsError}</div>}
          {orderDisplaySettingsError && <div style={{ fontSize:12, color:'#DC2626', fontWeight:700, padding:'0 16px 14px' }}>{orderDisplaySettingsError}</div>}
        </SettingsSection>
      </>
    ),
    invoices: (
      <>
        <SettingsPageTitle title="Invoices" subtitle="Set the seller details used when generating order invoices."/>
        <SettingsSection title="Invoice identity" subtitle="These details appear on every invoice generated from Orders.">
          {invoiceSettingsLoading ? (
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <SkeletonPill width="100%" height={64} style={{ borderRadius:8 }}/>
              <SkeletonPill width="92%" height={120} style={{ borderRadius:8 }}/>
            </div>
          ) : (
            <div style={{ padding:16, display:'grid', gap:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'160px minmax(0, 1fr)', gap:18, alignItems:'start' }}>
                <div>
                  <div style={{ width:150, height:90, border:'1px solid #E5E7EB', borderRadius:8, background:'#F9FAFB', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                    {invoiceSettings.logoData
                      ? <img src={invoiceSettings.logoData} alt="Invoice logo preview" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', padding:8 }}/>
                      : <span style={{ color:'#9CA3AF', fontSize:12, fontWeight:700 }}>No logo</span>
                    }
                  </div>
                  <div style={{ display:'flex', gap:7, marginTop:8, flexWrap:'wrap' }}>
                    <label style={{ ...softButtonStyle, padding:'7px 9px', cursor:'pointer' }}>
                      Upload logo
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={chooseInvoiceLogo} style={{ display:'none' }}/>
                    </label>
                    {invoiceSettings.logoData && (
                      <button type="button" onClick={() => updateInvoiceSetting('logoData', '')} style={{ ...softButtonStyle, padding:'7px 9px', color:'#B91C1C' }}>Remove</button>
                    )}
                  </div>
                  <div style={{ marginTop:6, fontSize:11, color:'#9CA3AF', lineHeight:1.35 }}>PNG, JPEG, or WebP. It is resized before saving.</div>
                </div>
                <div style={{ display:'grid', gap:12 }}>
                  <label style={{ display:'grid', gap:5 }}>
                    <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Business or seller name</span>
                    <input value={invoiceSettings.businessName} onChange={e => updateInvoiceSetting('businessName', e.target.value)} placeholder="Your business name" style={inputStyle}/>
                  </label>
                  <label style={{ display:'grid', gap:5 }}>
                    <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Business address</span>
                    <textarea value={invoiceSettings.address} onChange={e => updateInvoiceSetting('address', e.target.value)} placeholder="Street, city, postal code, country" rows={4} style={{ ...inputStyle, resize:'vertical', lineHeight:1.4 }}/>
                  </label>
                  <label style={{ display:'grid', gap:5 }}>
                    <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Goods dispatched from (optional)</span>
                    <textarea value={invoiceSettings.dispatchAddress} onChange={e => updateInvoiceSetting('dispatchAddress', e.target.value)} placeholder="Warehouse name, street, city, postal code, country" rows={3} style={{ ...inputStyle, resize:'vertical', lineHeight:1.4 }}/>
                    <span style={{ fontSize:11, color:'#9CA3AF' }}>Use this when goods leave from a warehouse that differs from the seller address.</span>
                  </label>
                  <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:12 }}>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Email</span>
                      <input type="email" value={invoiceSettings.email} onChange={e => updateInvoiceSetting('email', e.target.value)} placeholder="billing@example.com" style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Phone</span>
                      <input value={invoiceSettings.phone} onChange={e => updateInvoiceSetting('phone', e.target.value)} placeholder="+1 555 000 0000" style={inputStyle}/>
                    </label>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:12 }}>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>VAT or tax ID</span>
                      <input value={invoiceSettings.taxId} onChange={e => updateInvoiceSetting('taxId', e.target.value)} placeholder="Optional" style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>EORI</span>
                      <input value={invoiceSettings.eori} onChange={e => updateInvoiceSetting('eori', e.target.value)} placeholder="Exporter EORI" style={inputStyle}/>
                    </label>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:12 }}>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Invoice prefix</span>
                      <input value={invoiceSettings.invoicePrefix} onChange={e => updateInvoiceSetting('invoicePrefix', e.target.value)} placeholder="INV" maxLength={24} style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Authorized signer</span>
                      <input value={invoiceSettings.signerName} onChange={e => updateInvoiceSetting('signerName', e.target.value)} placeholder="Name shown below the declaration" style={inputStyle}/>
                    </label>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:12 }}>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Default reason for export</span>
                      <select value={invoiceSettings.defaultReasonExport} onChange={e => updateInvoiceSetting('defaultReasonExport', e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                        {COMMERCIAL_INVOICE_REASON_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Default Incoterm</span>
                      <select value={invoiceSettings.defaultIncoterm} onChange={e => updateInvoiceSetting('defaultIncoterm', e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                        <option value="">Choose per shipment</option>
                        {COMMERCIAL_INVOICE_INCOTERM_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                  </div>
                  <label style={{ display:'grid', gap:5 }}>
                    <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>VAT statement (optional)</span>
                    <textarea value={invoiceSettings.vatStatement} onChange={e => updateInvoiceSetting('vatStatement', e.target.value)} placeholder="e.g. Seller not registered for VAT. Export of goods outside the EU; no VAT charged." rows={2} style={{ ...inputStyle, resize:'vertical', lineHeight:1.4 }}/>
                  </label>
                  <label style={{ display:'grid', gap:5 }}>
                    <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Default freight statement (optional)</span>
                    <input value={invoiceSettings.defaultFreightStatement} onChange={e => updateInvoiceSetting('defaultFreightStatement', e.target.value)} placeholder="e.g. Freight included in DDP price" style={inputStyle}/>
                    <span style={{ fontSize:11, color:'#9CA3AF' }}>Prefills each invoice and can be changed for a specific shipment.</span>
                  </label>
                  <label style={{ display:'grid', gap:5 }}>
                    <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Default invoice note</span>
                    <textarea value={invoiceSettings.defaultNote} onChange={e => updateInvoiceSetting('defaultNote', e.target.value)} placeholder="Optional footer or payment note" rows={3} style={{ ...inputStyle, resize:'vertical', lineHeight:1.4 }}/>
                  </label>
                  <div>
                    <div style={{ fontSize:12, fontWeight:900, color:'#374151', marginBottom:6 }}>Authorized signature</div>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                      <div style={{ width:180, height:72, border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                        {invoiceSettings.signatureData
                          ? <img src={invoiceSettings.signatureData} alt="Invoice signature preview" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', padding:6 }}/>
                          : <span style={{ color:'#9CA3AF', fontSize:11, fontWeight:700 }}>No signature image</span>
                        }
                      </div>
                      <label style={{ ...softButtonStyle, padding:'7px 9px', cursor:'pointer' }}>
                        Upload signature
                        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={chooseInvoiceSignature} style={{ display:'none' }}/>
                      </label>
                      {invoiceSettings.signatureData && <button type="button" onClick={() => updateInvoiceSetting('signatureData', '')} style={{ ...softButtonStyle, padding:'7px 9px', color:'#B91C1C' }}>Remove</button>}
                    </div>
                  </div>
                </div>
              </div>
              {invoiceSettingsError && <div style={{ color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:10, fontSize:12, fontWeight:800 }}>{invoiceSettingsError}</div>}
              {invoiceSettingsNotice && <div style={{ color:'#065F46', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:10, fontSize:12, fontWeight:800 }}>{invoiceSettingsNotice}</div>}
              <div>
                <button type="button" onClick={saveInvoiceSettings} disabled={invoiceSettingsSaving} style={{ ...primaryButtonStyle, cursor:invoiceSettingsSaving ? 'wait' : 'pointer' }}>
                  {invoiceSettingsSaving ? 'Saving...' : 'Save invoice settings'}
                </button>
              </div>
            </div>
          )}
        </SettingsSection>
      </>
    ),
    integrations: (
      <>
        <SettingsPageTitle
          title="Integrations"
          right={<span style={gmailConnection ? statusPillStyle : neutralPillStyle}>{gmailConnection ? 'Gmail connected' : 'Not connected'}</span>}
        />
        <SettingsSection title="Gmail" subtitle="Send export-rule CSV files from a connected Gmail account.">
          {gmailLoading ? (
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <SkeletonPill width="100%" height={42} style={{ borderRadius:8 }}/>
              <SkeletonPill width="92%" height={120} style={{ borderRadius:8 }}/>
            </div>
          ) : (
            <div className="settings-agent-body">
              {gmailError && <div style={{ color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:10, fontSize:12, fontWeight:800, lineHeight:1.4 }}>{gmailError}</div>}
              {gmailNotice && <div style={{ color:'#065F46', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:10, fontSize:12, fontWeight:800, lineHeight:1.4 }}>{gmailNotice}</div>}
              <div className="settings-row" style={{ padding:0, borderTop:'none' }}>
                <div className="settings-row-main">
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Gmail account</div>
                    <span style={gmailConnection ? statusPillStyle : neutralPillStyle}>{gmailConnection ? gmailConnection.email || 'Connected' : 'Not connected'}</span>
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:3, lineHeight:1.4 }}>
                    {gmailConnection ? `Last checked ${formatAgentDate(gmailConnection.lastTestedAt || gmailConnection.updatedAt)}` : 'Connect Gmail before enabling email exports.'}
                  </div>
                </div>
                <div className="settings-action">
                  {gmailConnection ? (
                    <button type="button" onClick={disconnectGmail} disabled={gmailSaving} style={{ ...dangerButtonStyle, cursor:gmailSaving ? 'wait' : 'pointer' }}>
                      Disconnect
                    </button>
                  ) : (
                    <button type="button" onClick={connectGmail} disabled={!gmailState?.configured || gmailSaving} style={{ ...primaryButtonStyle, cursor:gmailState?.configured && !gmailSaving ? 'pointer' : 'not-allowed' }}>
                      Connect Gmail
                    </button>
                  )}
                </div>
              </div>

              <div className="settings-subsection">
                <h3>Email export</h3>
                <div className="settings-grid">
                  <div className="settings-stack">
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Export rule</span>
                      <select value={gmailDraft.ruleId} onChange={e => updateGmailDraft({ ruleId:e.target.value })} style={{ ...inputStyle, cursor:'pointer' }}>
                        <option value="">Choose a rule</option>
                        {gmailRules.map(rule => (
                          <option key={rule.id} value={rule.id}>
                            {rule.name} ({rule.matchCount || 0} rows)
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Recipients</span>
                      <input value={gmailDraft.recipientText} onChange={e => updateGmailDraft({ recipientText:e.target.value })} placeholder="warehouse@example.com, ops@example.com" style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Subject</span>
                      <input value={gmailDraft.subject} onChange={e => updateGmailDraft({ subject:e.target.value })} style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Message</span>
                      <textarea value={gmailDraft.message} onChange={e => updateGmailDraft({ message:e.target.value })} rows={4} style={{ ...inputStyle, resize:'vertical', lineHeight:1.4 }}/>
                    </label>
                  </div>
                  <div className="settings-stack">
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Schedule</span>
                      <select value={gmailDraft.triggerType} onChange={e => updateGmailDraft({ triggerType:e.target.value })} style={{ ...inputStyle, cursor:'pointer' }}>
                        <option value="interval">Every set interval</option>
                        <option value="new_order">Every new order</option>
                      </select>
                    </label>
                    {gmailDraft.triggerType !== 'new_order' && (
                      <label style={{ display:'grid', gap:5 }}>
                        <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Interval minutes</span>
                        <input type="number" min="15" max="10080" value={gmailDraft.intervalMinutes} onChange={e => updateGmailDraft({ intervalMinutes:e.target.value })} style={inputStyle}/>
                      </label>
                    )}
                    <div style={{ display:'grid', gap:10 }}>
                      <label style={{ display:'flex', alignItems:'flex-start', gap:9, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!gmailDraft.enabled} onChange={e => updateGmailDraft({ enabled:e.target.checked })} style={{ marginTop:2 }}/>
                        <span>
                          <span style={{ display:'block', fontSize:13, fontWeight:900, color:'#1A1A2E' }}>Enable email exports</span>
                          <span style={{ display:'block', fontSize:12, color:'#6B7280', lineHeight:1.35, marginTop:2 }}>{gmailDraft.triggerType === 'new_order' ? 'Send matching new orders after imports and source syncs.' : 'Send matching paid orders when the interval is due.'}</span>
                        </span>
                      </label>
                      <label style={{ display:'flex', alignItems:'flex-start', gap:9, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!gmailDraft.markRouted} onChange={e => updateGmailDraft({ markRouted:e.target.checked })} style={{ marginTop:2 }}/>
                        <span>
                          <span style={{ display:'block', fontSize:13, fontWeight:900, color:'#1A1A2E' }}>Mark emailed orders Routed</span>
                          <span style={{ display:'block', fontSize:12, color:'#6B7280', lineHeight:1.35, marginTop:2 }}>Stops the same paid orders from being sent again by default rules.</span>
                        </span>
                      </label>
                      <label style={{ display:'flex', alignItems:'flex-start', gap:9, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!gmailDraft.includeEmpty} onChange={e => updateGmailDraft({ includeEmpty:e.target.checked })} style={{ marginTop:2 }}/>
                        <span>
                          <span style={{ display:'block', fontSize:13, fontWeight:900, color:'#1A1A2E' }}>Send empty CSV when no orders match</span>
                        </span>
                      </label>
                    </div>
                    <div style={{ borderTop:'1px solid #F3F4F6', paddingTop:10, display:'grid', gap:6, color:'#6B7280', fontSize:12, lineHeight:1.4 }}>
                      <div>Selected rule: <strong style={{ color:'#374151' }}>{gmailSelectedRule?.name || 'None'}</strong></div>
                      <div>Last sent: <strong style={{ color:'#374151' }}>{formatAgentDate(gmailState?.schedule?.lastSentAt)}</strong></div>
                      {gmailState?.schedule?.nextRunAt && <div>Next interval: <strong style={{ color:'#374151' }}>{formatAgentDate(gmailState.schedule.nextRunAt)}</strong></div>}
                      {gmailState?.schedule?.lastError && <div style={{ color:'#B91C1C', fontWeight:800 }}>Last error: {gmailState.schedule.lastError}</div>}
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <button type="button" onClick={saveGmailSchedule} disabled={gmailSaving || !gmailConnection} style={{ ...primaryButtonStyle, cursor:gmailSaving || !gmailConnection ? 'not-allowed' : 'pointer' }}>
                    {gmailSaving ? 'Saving...' : 'Save email export'}
                  </button>
                  <button type="button" onClick={sendGmailExportNow} disabled={gmailSaving || !gmailCanSend} style={{ ...softButtonStyle, cursor:gmailSaving || !gmailCanSend ? 'not-allowed' : 'pointer', color:gmailCanSend ? '#374151' : '#9CA3AF' }}>
                    Send now
                  </button>
                </div>
              </div>
            </div>
          )}
        </SettingsSection>
        <SettingsSection title="Brevo" subtitle="Send order confirmation emails through a connected Brevo account.">
          {brevoLoading ? (
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <SkeletonPill width="100%" height={42} style={{ borderRadius:8 }}/>
              <SkeletonPill width="92%" height={120} style={{ borderRadius:8 }}/>
            </div>
          ) : (
            <div className="settings-agent-body">
              {brevoError && <div style={{ color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:10, fontSize:12, fontWeight:800, lineHeight:1.4 }}>{brevoError}</div>}
              {brevoNotice && <div style={{ color:'#065F46', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:10, fontSize:12, fontWeight:800, lineHeight:1.4 }}>{brevoNotice}</div>}
              <div className="settings-row" style={{ padding:0, borderTop:'none' }}>
                <div className="settings-row-main">
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Brevo account</div>
                    <span style={brevoState?.active ? statusPillStyle : neutralPillStyle}>{brevoState?.active ? 'Connected' : 'Not connected'}</span>
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:3, lineHeight:1.4 }}>
                    {brevoState?.lastTestedAt
                      ? `Last tested ${formatAgentDate(brevoState.lastTestedAt)}${brevoState.lastTestStatus === 'error' ? ' (failed)' : ''}`
                      : 'Enter an API key and save to connect.'}
                  </div>
                  {brevoState?.lastError && brevoState.lastTestStatus === 'error' && (
                    <div style={{ fontSize:12, color:'#B91C1C', marginTop:3, lineHeight:1.4 }}>{brevoState.lastError}</div>
                  )}
                </div>
                {brevoState?.active && (
                  <div className="settings-action">
                    <button type="button" onClick={disconnectBrevoConnection} disabled={brevoSaving} style={{ ...dangerButtonStyle, cursor:brevoSaving ? 'wait' : 'pointer' }}>
                      Disconnect
                    </button>
                  </div>
                )}
              </div>

              <div className="settings-subsection">
                <div className="settings-grid">
                  <div className="settings-stack">
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>API key</span>
                      <input type="password" value={brevoForm.apiKey} onChange={e => updateBrevoForm({ apiKey:e.target.value })} placeholder={brevoState?.hasApiKey ? 'Saved — enter a new key to replace it' : 'xkeysib-...'} style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Sender name</span>
                      <input value={brevoForm.senderName} onChange={e => updateBrevoForm({ senderName:e.target.value })} placeholder="Your Store" style={inputStyle}/>
                    </label>
                  </div>
                  <div className="settings-stack">
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Sender email</span>
                      <input type="email" value={brevoForm.senderEmail} onChange={e => updateBrevoForm({ senderEmail:e.target.value })} placeholder="orders@yourstore.com" style={inputStyle}/>
                    </label>
                    <label style={{ display:'grid', gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Fallback template ID</span>
                      <input type="number" min="1" value={brevoForm.fallbackTemplateId} onChange={e => updateBrevoForm({ fallbackTemplateId:e.target.value })} placeholder="Optional" style={inputStyle}/>
                    </label>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <button type="button" onClick={saveBrevoConnection} disabled={brevoSaving} style={{ ...primaryButtonStyle, cursor:brevoSaving ? 'wait' : 'pointer' }}>
                    {brevoSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={testBrevoConnection} disabled={brevoTesting || !brevoState?.hasApiKey} style={{ ...softButtonStyle, cursor:brevoTesting || !brevoState?.hasApiKey ? 'not-allowed' : 'pointer', color:brevoState?.hasApiKey ? '#374151' : '#9CA3AF' }}>
                    {brevoTesting ? 'Testing...' : 'Test connection'}
                  </button>
                </div>
                {brevoState?.webhookPath && (
                  <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #F3F4F6' }}>
                    <div style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Bounce &amp; spam webhook (optional)</div>
                    <div style={{ fontSize:12, color:'#6B7280', marginTop:3, lineHeight:1.4 }}>Add this path, on your Omnifill API host, as a webhook in Brevo under Transactional &rarr; Settings &rarr; Webhooks so bounces and spam complaints suppress future emails to that address.</div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, flexWrap:'wrap' }}>
                      <code style={{ fontSize:11, background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:6, padding:'6px 8px', color:'#374151', wordBreak:'break-all' }}>{brevoState.webhookPath}</code>
                      <button type="button" onClick={() => navigator.clipboard?.writeText(brevoState.webhookPath).catch(() => {})} style={{ ...softButtonStyle, padding:'5px 9px' }}>
                        Copy path
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SettingsSection>
      </>
    ),
    billing: (
      <>
        <SettingsPageTitle
          title="Billing"
          right={<span style={{ display:'inline-flex', alignItems:'center', border:`1px solid ${statusColor === '#065F46' ? '#BBF7D0' : '#E5E7EB'}`, background:statusBg, color:statusColor, borderRadius:8, padding:'6px 9px', fontSize:12, fontWeight:800, whiteSpace:'nowrap' }}>{subscriptionLabel}</span>}
        />
        <SettingsSection title="Plan">
          <SettingsItem title={periodLabel} detail={subExpiry}/>
          <div style={{ padding:16, borderTop:'1px solid #F3F4F6' }}>
            <div className="settings-card-grid">
              <BillingPlanCard
                title="Pro"
                price="$29/mo"
                tone="blue"
                detail={proBillingDetail}
                badge={hasActiveAccess && <span style={statusPillStyle}>{sub?.status === 'trialing' ? 'Trial active' : 'Current'}</span>}
                features={proFeatures}
                action={
                  hasActiveAccess && canManageBilling ? (
                    <button type="button" onClick={openBillingPortal} disabled={!!upgradeBusy} style={{ ...primaryButtonStyle, cursor:upgradeBusy ? 'wait' : 'pointer' }}>
                      {upgradeBusy === 'portal' ? 'Opening...' : 'Manage billing'}
                    </button>
                  ) : hasActiveAccess ? (
                    <div style={{ display:'inline-flex', alignItems:'center', gap:7, color:'#065F46', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'8px 10px', fontSize:12, fontWeight:900 }}>
                      No checkout needed
                    </div>
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <button type="button" onClick={() => startUpgrade('monthly')} disabled={!!upgradeBusy} style={{ ...softButtonStyle, cursor:upgradeBusy ? 'wait' : 'pointer' }}>
                        {upgradeBusy === 'monthly' ? 'Opening...' : 'Monthly'}
                      </button>
                      <button type="button" onClick={() => startUpgrade('yearly')} disabled={!!upgradeBusy} style={{ ...primaryButtonStyle, cursor:upgradeBusy ? 'wait' : 'pointer' }}>
                        {upgradeBusy === 'yearly' ? 'Opening...' : 'Annual'}
                      </button>
                    </div>
                  )
                }
              />
              <BillingPlanCard
                title="Enterprise"
                price="Custom"
                tone="purple"
                detail="For higher-volume operations, priority support, and custom fulfillment workflows."
                features={['Personalized onboarding', 'Priority support', 'Custom API integrations where feasible']}
                action={
                  <a href="https://omnifill.net/enterprise" style={{ ...softButtonStyle, textDecoration:'none', justifyContent:'center' }}>
                    Contact sales
                  </a>
                }
              />
            </div>
            {upgradeError && <div style={{ color:'#B91C1C', fontSize:12, fontWeight:700, marginTop:10, lineHeight:1.4 }}>{upgradeError}</div>}
          </div>
        </SettingsSection>
      </>
    ),
    assistant: (
      <>
        <SettingsPageTitle title="Assistant"/>
        <SettingsSection title="Weekly budget">
          <div style={{ padding:16, display:'grid', gap:10 }}>
            {assistantUsageLoading ? (
              <SkeletonPill width="100%" height={46} style={{ borderRadius:8 }}/>
            ) : assistantUsageError ? (
              <div style={{ color:'#B91C1C', fontSize:12, fontWeight:700, lineHeight:1.4 }}>{assistantUsageError}</div>
            ) : (
              <>
                <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                  <div style={{ fontSize:13, lineHeight:1.45, color:'#374151' }}>
                    <strong style={{ color:'#1A1A2E', fontWeight:900 }}>{formatAssistantPct(assistantBudgetPct)} used this week.</strong>
                  </div>
                  <div style={{ fontSize:12, fontWeight:800, color:'#6B7280' }}>
                    Resets {assistantResetLabel || 'next week'}
                  </div>
                </div>
                {assistantUsage?.budgetMicros > 0 && (
                  <div style={{ height:7, borderRadius:8, background:'#E5E7EB', overflow:'hidden' }}>
                    <div style={{ width:`${assistantBudgetPct}%`, height:'100%', background:assistantBudgetPct >= 90 ? '#DC2626' : assistantBudgetPct >= 70 ? '#F59E0B' : '#006FDD', borderRadius:8 }}></div>
                  </div>
                )}
              </>
            )}
          </div>
        </SettingsSection>
      </>
    ),
    apikeys: (
      <>
        <SettingsPageTitle title="API keys"/>
        <SettingsSection title="Keys">
          {apiKeysLoading ? (
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <SkeletonPill width="100%" height={42} style={{ borderRadius:8 }}/>
              <SkeletonPill width="88%" height={64} style={{ borderRadius:8 }}/>
            </div>
          ) : (
            <div className="settings-agent-body">
              {apiKeysError && <div style={{ color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:10, fontSize:12, fontWeight:800, lineHeight:1.4 }}>{apiKeysError}</div>}

              <div style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:11, background:'#F9FAFB', color:'#374151', fontSize:12, lineHeight:1.5 }}>
                Read-only keys for pulling data into your own site or scripts (e.g. a live stock count badge). Send the key as <code>Authorization: Bearer &lt;key&gt;</code>.
              </div>

              <div className="settings-subsection">
                <h3>Create a key</h3>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                  <input value={newApiKeyName} onChange={e => { setNewApiKeyName(e.target.value); setCreatedApiKey(null); }} placeholder="Key name (e.g. founders-stock)" style={{ ...inputStyle, maxWidth:260 }}/>
                  <button type="button" onClick={createApiKey} disabled={apiKeysSaving || !newApiKeyName.trim()} style={{ ...softButtonStyle, background:newApiKeyName.trim() ? '#006FDD' : '#F9FAFB', color:newApiKeyName.trim() ? '#fff' : '#9CA3AF', borderColor:newApiKeyName.trim() ? '#006FDD' : '#E5E7EB', cursor:apiKeysSaving || !newApiKeyName.trim() ? 'not-allowed' : 'pointer' }}>
                    {apiKeysSaving ? 'Creating...' : 'Create key'}
                  </button>
                </div>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:8 }}>
                  {['products', 'stock', 'email_validation'].map(capability => {
                    const label = capability === 'email_validation' ? 'Email validation' : capability === 'products' ? 'Products' : 'Stock';
                    return (
                      <label key={capability} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'#374151', fontWeight:800, cursor:'pointer' }}>
                        <input type="checkbox" checked={newApiKeyCapabilities.includes(capability)} onChange={e => {
                          setNewApiKeyCapabilities(prev => e.target.checked
                            ? [...prev, capability]
                            : prev.filter(value => value !== capability));
                        }}/>
                        {label}
                      </label>
                    );
                  })}
                </div>
                {createdApiKey?.key && (
                  <div style={{ border:'1px solid #BFDBFE', background:'#F8FBFF', borderRadius:8, padding:10, display:'grid', gap:8 }}>
                    <div style={{ fontSize:12, fontWeight:900, color:'#1E3A8A' }}>New key for {createdApiKey.name} - copy it now, it will not be shown again</div>
                    <code style={{ display:'block', border:'1px solid #D7E7FF', borderRadius:7, padding:8, background:'#fff', color:'#111827', fontSize:11, lineHeight:1.45, wordBreak:'break-all' }}>{createdApiKey.key}</code>
                    <button type="button" onClick={copyCreatedApiKey} style={{ ...softButtonStyle, width:'fit-content' }}>
                      Copy key
                    </button>
                  </div>
                )}
                <div style={{ display:'grid', gap:8 }}>
                  {apiKeys.filter(key => key.status === 'active').length === 0 ? (
                    <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.4 }}>No API keys have been created.</div>
                  ) : apiKeys.filter(key => key.status === 'active').map(key => (
                    <div key={key.id} className="settings-token-row">
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:900, color:'#1A1A2E' }}>{key.name}</div>
                        <div style={{ fontSize:11, color:'#6B7280', marginTop:4, lineHeight:1.35 }}>
                          {key.keyPrefix}... - Last used {formatAgentDate(key.lastUsedAt)}
                        </div>
                        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:8 }}>
                          {['products', 'stock', 'email_validation'].map(capability => {
                            const label = capability === 'email_validation' ? 'Email validation' : capability === 'products' ? 'Products' : 'Stock';
                            const selected = (apiKeyDrafts[key.id] || []).includes(capability);
                            return (
                              <label key={`${key.id}-${capability}`} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:11, color:'#374151', fontWeight:800, cursor:'pointer' }}>
                                <input type="checkbox" checked={selected} onChange={e => {
                                  setApiKeyDrafts(prev => {
                                    const current = prev[key.id] || [];
                                    const next = current.filter(value => value !== capability);
                                    if (e.target.checked) next.push(capability);
                                    return { ...prev, [key.id]: next };
                                  });
                                }}/>
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        <button type="button" onClick={() => saveApiKeySettings(key.id)} disabled={apiKeysSaving} style={{ ...softButtonStyle, cursor:apiKeysSaving ? 'wait' : 'pointer' }}>
                          Save
                        </button>
                        <button type="button" onClick={() => revokeApiKey(key.id)} disabled={apiKeysSaving} style={{ ...dangerButtonStyle, cursor:apiKeysSaving ? 'wait' : 'pointer' }}>
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SettingsSection>
      </>
    ),
    agent: (
      <>
        <SettingsPageTitle
          title="Agent access"
          right={<span style={agentSettings.enabled ? statusPillStyle : neutralPillStyle}>{agentSettings.enabled ? 'Enabled' : 'Off'}</span>}
        />
        <SettingsSection title="Controls">
          {agentLoading ? (
            <div style={{ padding:16, display:'grid', gap:12 }}>
              <SkeletonPill width="100%" height={42} style={{ borderRadius:8 }}/>
              <SkeletonPill width="92%" height={82} style={{ borderRadius:8 }}/>
              <SkeletonPill width="88%" height={64} style={{ borderRadius:8 }}/>
            </div>
          ) : (
            <div className="settings-agent-body">
              {agentError && <div style={{ color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:10, fontSize:12, fontWeight:800, lineHeight:1.4 }}>{agentError}</div>}

              <div style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:11, background:'#F9FAFB', color:'#374151', fontSize:12, lineHeight:1.5 }}>
                <strong style={{ color:'#1A1A2E', fontWeight:900 }}>{agentSettings.enabled ? 'Agent hooks are enabled.' : 'Agent hooks are off.'}</strong>
                <span> {agentSettings.requireApproval ? 'Requests wait for review before work proceeds.' : 'Allowed requests can proceed without manual review.'}</span>
              </div>

              <div className="settings-subsection">
                <h3>Permissions</h3>
                <div style={{ display:'grid', gap:10 }}>
                  <label style={{ display:'flex', alignItems:'flex-start', gap:9, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!agentDraft.enabled} onChange={e => setAgentDraft(prev => ({ ...prev, enabled:e.target.checked }))} style={{ marginTop:2 }}/>
                    <span>
                      <span style={{ display:'block', fontSize:13, fontWeight:900, color:'#1A1A2E' }}>Allow agent hooks</span>
                      <span style={{ display:'block', fontSize:12, color:'#6B7280', lineHeight:1.35, marginTop:2 }}>When off, existing tokens cannot submit action requests.</span>
                    </span>
                  </label>
                  <label style={{ display:'flex', alignItems:'flex-start', gap:9, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!agentDraft.requireApproval} onChange={e => setAgentDraft(prev => ({ ...prev, requireApproval:e.target.checked }))} style={{ marginTop:2 }}/>
                    <span>
                      <span style={{ display:'block', fontSize:13, fontWeight:900, color:'#1A1A2E' }}>Require approval before work proceeds</span>
                      <span style={{ display:'block', fontSize:12, color:'#6B7280', lineHeight:1.35, marginTop:2 }}>High-risk fulfillment actions always stay in the review queue.</span>
                    </span>
                  </label>
                </div>
                <label style={{ display:'grid', gap:5, maxWidth:210 }}>
                  <span style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Max action requests per day</span>
                  <input type="number" min="1" max="100" value={agentDraft.maxActionsPerDay} onChange={e => setAgentDraft(prev => ({ ...prev, maxActionsPerDay:e.target.value }))} style={inputStyle}/>
                </label>
                <div style={{ display:'grid', gap:8 }}>
                  <div style={{ fontSize:12, fontWeight:900, color:'#374151' }}>Workspace permission scopes</div>
                  <div className="settings-scope-grid">
                    {agentScopes.map(scope => (
                      <label key={scope.id} style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:9, display:'grid', gridTemplateColumns:'18px minmax(0, 1fr)', gap:8, cursor:'pointer', background:agentDraft.allowedScopes.includes(scope.id) ? '#F8FBFF' : '#fff' }}>
                        <input type="checkbox" checked={agentDraft.allowedScopes.includes(scope.id)} onChange={() => toggleAgentScope(scope.id)} style={{ marginTop:2 }}/>
                        <span style={{ minWidth:0 }}>
                          <span style={{ display:'block', fontSize:12, fontWeight:900, color:'#1A1A2E' }}>{scope.label}</span>
                          <span style={{ display:'block', fontSize:11, color:'#6B7280', lineHeight:1.35, marginTop:2 }}>{scope.detail}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <button type="button" onClick={saveAgentSettings} disabled={agentSaving} style={{ ...primaryButtonStyle, cursor:agentSaving ? 'wait' : 'pointer' }}>
                    {agentSaving ? 'Saving...' : 'Save agent controls'}
                  </button>
                </div>
              </div>

              <div className="settings-subsection">
                <h3>Tokens</h3>
                <p>Create one token for each external agent. New tokens are shown once.</p>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                  <input value={newAgentName} onChange={e => { setNewAgentName(e.target.value); setCreatedAgentToken(null); }} placeholder="Agent name" style={{ ...inputStyle, maxWidth:260 }}/>
                  <button type="button" onClick={createAgentToken} disabled={agentSaving || !newAgentName.trim() || !newAgentScopes.length} style={{ ...softButtonStyle, background:newAgentName.trim() && newAgentScopes.length ? '#006FDD' : '#F9FAFB', color:newAgentName.trim() && newAgentScopes.length ? '#fff' : '#9CA3AF', borderColor:newAgentName.trim() && newAgentScopes.length ? '#006FDD' : '#E5E7EB', cursor:agentSaving || !newAgentName.trim() || !newAgentScopes.length ? 'not-allowed' : 'pointer' }}>
                    {agentSaving ? 'Creating...' : 'Create token'}
                  </button>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                  {agentScopes.filter(scope => agentSettings.allowedScopes?.includes(scope.id)).map(scope => (
                    <label key={scope.id} style={{ display:'inline-flex', alignItems:'center', gap:6, border:'1px solid #E5E7EB', borderRadius:8, padding:'6px 8px', fontSize:11, fontWeight:800, color:'#374151', background:newAgentScopes.includes(scope.id) ? '#EEF6FF' : '#fff', cursor:'pointer' }}>
                      <input type="checkbox" checked={newAgentScopes.includes(scope.id)} onChange={() => toggleNewAgentScope(scope.id)}/>
                      {scope.label}
                    </label>
                  ))}
                </div>
                {createdAgentToken?.token && (
                  <div style={{ border:'1px solid #BFDBFE', background:'#F8FBFF', borderRadius:8, padding:10, display:'grid', gap:8 }}>
                    <div style={{ fontSize:12, fontWeight:900, color:'#1E3A8A' }}>New token for {createdAgentToken.name}</div>
                    <code style={{ display:'block', border:'1px solid #D7E7FF', borderRadius:7, padding:8, background:'#fff', color:'#111827', fontSize:11, lineHeight:1.45, wordBreak:'break-all' }}>{createdAgentToken.token}</code>
                    <button type="button" onClick={copyCreatedAgentToken} style={{ ...softButtonStyle, width:'fit-content' }}>
                      Copy token
                    </button>
                  </div>
                )}
                <div style={{ display:'grid', gap:8 }}>
                  {agentTokens.length === 0 ? (
                    <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.4 }}>No agent tokens have been created.</div>
                  ) : agentTokens.map(token => (
                    <div key={token.id} className="settings-token-row">
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:900, color:'#1A1A2E' }}>{token.name}</div>
                        <div style={{ fontSize:11, color:'#6B7280', marginTop:4, lineHeight:1.35 }}>
                          {token.tokenPrefix}... - {token.scopes.map(scopeLabel).join(', ') || 'No scopes'} - Last used {formatAgentDate(token.lastUsedAt)}
                        </div>
                      </div>
                      <button type="button" onClick={() => revokeAgentToken(token.id)} disabled={agentSaving} style={{ ...dangerButtonStyle, cursor:agentSaving ? 'wait' : 'pointer' }}>
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="settings-subsection">
                <h3>Supervision queue</h3>
                <p>Pending requests wait here until approved or rejected.</p>
                {pendingAgentRequests.length === 0 ? (
                  <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.4 }}>No agent requests need review.</div>
                ) : pendingAgentRequests.map(request => (
                  <div key={request.id} style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:10, display:'grid', gap:8, background:'#FFFBEB' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:900, color:'#1A1A2E' }}>{request.actionLabel}</div>
                        <div style={{ fontSize:11, color:'#6B7280', marginTop:3 }}>{request.agentName} - {scopeLabel(request.scope)} - {formatAgentDate(request.requestedAt)}</div>
                      </div>
                      <span style={{ ...neutralPillStyle, color:request.riskLevel === 'high' ? '#B91C1C' : request.riskLevel === 'medium' ? '#92400E' : '#065F46' }}>{request.riskLevel} risk</span>
                    </div>
                    {request.summary && <div style={{ fontSize:12, color:'#374151', lineHeight:1.4 }}>{request.summary}</div>}
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <button type="button" onClick={() => reviewAgentRequest(request.id, 'approve')} disabled={agentSaving} style={{ ...primaryButtonStyle, background:'#16A34A', borderColor:'#16A34A', cursor:agentSaving ? 'wait' : 'pointer' }}>
                        Approve
                      </button>
                      <button type="button" onClick={() => reviewAgentRequest(request.id, 'reject')} disabled={agentSaving} style={{ ...dangerButtonStyle, cursor:agentSaving ? 'wait' : 'pointer' }}>
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SettingsSection>
      </>
    ),
    data: (
      <>
        <SettingsPageTitle title="Data requests"/>
        <SettingsSection title="Account data">
          <SettingsItem
            title="Account deletion or data purge"
            detail="Create a support ticket. We will verify the request and confirm the next steps before taking action."
            action={
              <button type="button" onClick={openDataRequestTicket} style={{ ...primaryButtonStyle }}>
                Create ticket
              </button>
            }
          />
        </SettingsSection>
      </>
    ),
  };

  return (
    <div className="dash-page-scroll workspace-page settings-shell">
      <div className="dash-panel workspace-panel-flat settings-layout-panel">
        <div className="dash-panel-header settings-main-header">
          <h1 className="dash-page-title">Settings</h1>
          <p className="dash-page-subtitle">Manage account, billing, and workspace defaults.</p>
        </div>
        <div className="settings-layout">
          <aside className="settings-nav-panel" aria-label="Settings sections">
            <div className="settings-account-card">
              <div style={{ minWidth:0 }}>
                <div className="settings-account-name">{user?.email || 'Account'}</div>
                <div className="settings-account-email">{user?.emailVerified ? 'Verified email' : 'Email not verified'}</div>
              </div>
            </div>
            <nav className="settings-nav-list">
              {settingsPages.map(page => {
                const active = page.id === activeSettingsConfig.id;
                const Icon = page.Icon;
                return (
                  <button
                    key={page.id}
                    type="button"
                    className={`settings-nav-button${active ? ' active' : ''}`}
                    onClick={() => setActiveSettingsPage(page.id)}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="settings-nav-icon" aria-hidden="true">
                      <Icon size={23} strokeWidth={2.1}/>
                    </span>
                    <span className="settings-nav-text">
                      <span className="settings-nav-label">{page.label}</span>
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>
          <main className="settings-content-panel">
            {settingsPageContent[activeSettingsConfig.id] || settingsPageContent.account}
          </main>
        </div>
      </div>
    </div>
  );
};

const isoDate = d => d.toISOString().slice(0, 10);
const parseIsoDay = iso => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const addDays = (d, days) => {
  const next = new Date(d);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};
const fillDailyAnalytics = (rows = [], range = 'all', applied = null) => {
  const byDate = new Map(rows.map(r => [r.date, r]));
  const dates = rows.map(r => parseIsoDay(r.date)).filter(Boolean).sort((a, b) => a - b);
  if (range === 'all' && dates.length === 0) return [];

  const today = new Date();
  let start;
  let end;
  if (range === 'custom' && applied?.from && applied?.to) {
    start = parseIsoDay(applied.from);
    end = parseIsoDay(applied.to);
  } else if (range !== 'all') {
    end = parseIsoDay(isoDate(today));
    start = addDays(end, -Number(range || 0));
  } else {
    start = dates[0];
    end = dates[dates.length - 1];
  }
  if (!start || !end || start > end) return rows;

  const filled = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    const date = isoDate(d);
    const row = byDate.get(date);
    filled.push({ date, count: Number(row?.count || 0), revenue: Number(row?.revenue || 0) });
  }
  return filled;
};

const COUNTRY_CODE_BY_NAME = {
  'united states': 'US', 'usa': 'US', 'us': 'US', 'u.s.': 'US', 'u.s.a.': 'US',
  'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB', 'england': 'GB',
  'canada': 'CA', 'australia': 'AU', 'new zealand': 'NZ',
  'germany': 'DE', 'france': 'FR', 'spain': 'ES', 'italy': 'IT', 'netherlands': 'NL',
  'belgium': 'BE', 'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK', 'finland': 'FI',
  'ireland': 'IE', 'switzerland': 'CH', 'austria': 'AT', 'poland': 'PL',
  'portugal': 'PT', 'japan': 'JP', 'china': 'CN', 'hong kong': 'HK', 'singapore': 'SG',
  'south korea': 'KR', 'korea': 'KR', 'india': 'IN', 'mexico': 'MX', 'brazil': 'BR',
};
const countryCodeFromValue = value => {
  const raw = String(value || '').trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  if (/^[A-Za-z]{3}$/.test(raw) && raw.toUpperCase() === 'USA') return 'US';
  return COUNTRY_CODE_BY_NAME[raw.toLowerCase()] || '';
};
const countryFlagSrc = value => {
  const code = countryCodeFromValue(value);
  return code ? `/assets/circle-flags/flags/${code.toLowerCase()}.svg` : '';
};
const countryName = value => {
  const raw = String(value || '').trim();
  if (!raw) return 'Unknown';
  const code = countryCodeFromValue(raw);
  if (raw.length <= 3 && code) return code;
  return raw;
};
const countryDisplayName = value => {
  const code = countryCodeFromValue(value);
  if (!code) return countryName(value);
  try {
    return new Intl.DisplayNames(['en'], { type:'region' }).of(code) || code;
  } catch {
    return code;
  }
};
const GOOGLE_MAPS_AUTH_FAILURE_EVENT = 'omnifill-google-maps-auth-failure';
let googleMapsLoadPromise = null;
const loadGoogleMaps = apiKey => {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (!apiKey) return Promise.reject(new Error('Google Maps is not configured.'));
  if (!googleMapsLoadPromise) {
    googleMapsLoadPromise = new Promise((resolve, reject) => {
      const callbackName = '__omnifillGoogleMapsReady';
      const script = document.createElement('script');
      const query = new URLSearchParams({
        key:apiKey,
        v:'weekly',
        loading:'async',
        callback:callbackName,
      });
      window.gm_authFailure = () => {
        window.dispatchEvent(new CustomEvent(GOOGLE_MAPS_AUTH_FAILURE_EVENT));
      };
      window[callbackName] = () => {
        delete window[callbackName];
        resolve(window.google.maps);
      };
      script.id = 'google-maps-loader';
      script.src = `https://maps.googleapis.com/maps/api/js?${query}`;
      script.async = true;
      script.onerror = () => {
        delete window[callbackName];
        googleMapsLoadPromise = null;
        reject(new Error('Google Maps could not be loaded.'));
      };
      document.head.appendChild(script);
    });
  }
  return googleMapsLoadPromise;
};
